import React, { useState } from "react";
import { Box } from "@mui/system";
import { useSelector } from "react-redux";
import GridTable from "../../table/gridTable";
import Loader from "../../SharedLayouts/Loader";
import CustomButton from "../../Buttons/Button";
import ApproveOrderModal from "../../modal/ApproveOrderModal";
import RejectOrderModal from "../../modal/RejectOrderModal";
import ExportCsvTable from "../../Export/ExportCsvTable";
import { getCurrencySymbol } from "../../../Helper/getCurrencySymbol";

const statusStyle = (status) => {
  const value = (status || "").toLowerCase();
  if (value === "approved" || value === "executed" || value === "completed") {
    return { color: "#067647", backgroundColor: "#ECFDF3" };
  }
  if (value === "rejected" || value === "cancelled" || value === "failed") {
    return { color: "#B42318", backgroundColor: "#FECDCA" };
  }
  return { color: "#B54708", backgroundColor: "#FFFAEB" };
};

function OrdersTable() {
  const orders = useSelector((state) => state?.stocks?.orders) || [];
  const loading = useSelector((state) => state?.stocks?.ordersPending);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showApprove, setShowApprove] = useState(false);
  const [showReject, setShowReject] = useState(false);

  const isPending = (status) => {
    const value = (status || "").toLowerCase();
    return value === "" || value === "pending" || value === "processing";
  };

  const columns = [
    { field: "id", headerName: "Order ID", width: 90 },
    {
      field: "user",
      headerName: "User",
      width: 180,
      renderCell: (params) =>
        params.row?.user?.name ||
        params.row?.user_name ||
        params.row?.user?.email ||
        "-",
    },
    {
      field: "symbol",
      headerName: "Symbol",
      width: 120,
      renderCell: (params) =>
        params.row?.stock?.symbol || params.row?.symbol || "-",
    },
    {
      field: "side",
      headerName: "Type",
      width: 100,
      renderCell: (params) => (
        <span style={{ textTransform: "capitalize" }}>
          {params.row?.side ||
            params.row?.type ||
            params.row?.order_type ||
            "-"}
        </span>
      ),
    },
    {
      field: "volume",
      headerName: "Volume",
      width: 110,
      renderCell: (params) => params.row?.volume ?? params.row?.quantity ?? "-",
    },
    {
      field: "price",
      headerName: "Price Per Stock",
      width: 130,
      renderCell: (params) => {
        const value = params.row?.fee_breakdown?.price;
        return `${getCurrencySymbol(params.row?.currency)} ${
          value || value === 0 ? Number(value).toLocaleString() : "-"
        }`;
      },
    },

    {
      field: "cost",
      headerName: "Consideration/Cost",
      width: 130,
      renderCell: (params) => {
        const value = params.row?.consideration;
        return `${getCurrencySymbol(params.row?.currency)} ${
          value || value === 0 ? Number(value).toLocaleString() : "-"
        }`;
      },
    },
    {
      field: "total_fee",
      headerName: "Total Fee",
      width: 130,
      renderCell: (params) => {
        const value = params.row?.total_fees;
        return `${getCurrencySymbol(params.row?.currency)} ${
          value || value === 0 ? Number(value).toLocaleString() : "-"
        }`;
      },
    },
    {
      field: "total_amount",
      headerName: "Total Amount",
      width: 130,
      renderCell: (params) => {
        const value = params.row?.total_fees + params.row?.consideration;
        return `${getCurrencySymbol(params.row?.currency)} ${
          value || value === 0 ? Number(value).toLocaleString() : "-"
        }`;
      },
    },
    {
      field: "brokers",
      headerName: "Broker's Commission",
      width: 130,
      renderCell: (params) => {
        const value = params.row?.fee_breakdown?.broker_commission;
        return `${getCurrencySymbol(params.row?.currency)} ${
          value || value === 0 ? Number(value).toLocaleString() : "-"
        }`;
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <div
          style={{
            display: "inline-block",
            padding: "0 8px",
            height: "22px",
            borderRadius: "10px",
            textAlign: "center",
            lineHeight: "22px",
            textTransform: "capitalize",
            ...statusStyle(params.row?.status),
          }}
        >
          {params.row?.status || "Pending"}
        </div>
      ),
    },
    {
      field: "created_at",
      headerName: "Date",
      width: 160,
      renderCell: (params) =>
        params.row?.created_at
          ? new Date(params.row.created_at).toLocaleDateString()
          : "-",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      sortable: false,
      renderCell: (params) =>
        isPending(params.row?.status) ? (
          <div className="flex items-center gap-2">
            <CustomButton
              variant="contained"
              customColor="#159AA8"
              children="Approve"
              width="80px"
              height="32px"
              color="#fff"
              fz="12px"
              onClick={() => {
                setSelectedOrder(params.row);
                setShowApprove(true);
              }}
            />
            <CustomButton
              variant="outlined"
              customColor="#fff"
              children="Reject"
              width="80px"
              height="32px"
              borderColor="#B42318"
              color="#B42318"
              fz="12px"
              onClick={() => {
                setSelectedOrder(params.row);
                setShowReject(true);
              }}
            />
          </div>
        ) : (
          <span className="text-[#475467]">—</span>
        ),
    },
  ];

  const csvHeaders = [
    "Order ID",
    "User",
    "Symbol",
    "Type",
    "Volume",
    "Price Per Stock",
    "Consideration/Cost",
    "Total Fee",
    "Total Amount",
    "Broker's Commission",
    "Status",
    "Date",
  ];

  const formatAmount = (currency, value) =>
    `${getCurrencySymbol(currency)} ${
      value || value === 0 ? Number(value).toLocaleString() : "-"
    }`;

  const csvRows = orders.map((row) => [
    row?.id,
    row?.user?.name || row?.user_name || row?.user?.email || "-",
    row?.stock?.symbol || row?.symbol || "-",
    row?.side || row?.type || row?.order_type || "-",
    row?.volume ?? row?.quantity ?? "-",
    formatAmount(row?.currency, row?.fee_breakdown?.price),
    formatAmount(row?.currency, row?.consideration),
    formatAmount(row?.currency, row?.total_fees),
    formatAmount(row?.currency, row?.total_fees + row?.consideration),
    formatAmount(row?.currency, row?.fee_breakdown?.broker_commission),
    row?.status || "Pending",
    row?.created_at ? new Date(row.created_at).toLocaleDateString() : "-",
  ]);

  if (loading && orders?.length < 1) {
    return <Loader />;
  }

  return (
    <Box sx={{ overflowX: "auto", width: "100%" }}>
      <div className="flex justify-end mb-2">
        <ExportCsvTable
          headers={csvHeaders}
          rows={csvRows}
          filename="Stock-Orders.csv"
        />
      </div>
      <GridTable
        rows={orders}
        columns={columns}
        type={"stock-orders"}
        searchPlaceholder={"Search for Orders"}
        noReport
      />

      <ApproveOrderModal
        open={showApprove}
        order={selectedOrder}
        onClose={() => setShowApprove(false)}
      />
      <RejectOrderModal
        open={showReject}
        order={selectedOrder}
        onClose={() => setShowReject(false)}
      />
    </Box>
  );
}

export default OrdersTable;
